
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Question } from "@/types/theme";
import { useToast } from "@/components/ui/use-toast";

export const useQuestionMutations = (id: string | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const addQuestion = useMutation({
    mutationFn: async (question: Omit<Question, 'id' | 'chatbot_id'>) => {
      if (!id) throw new Error("No chatbot ID provided");

      // Create validation settings object
      let responsevalidation = question.responsevalidation;

      // If validationType or validation_rules are provided, store them in responsevalidation
      if (question.validationType || question.validation_rules) {
        const validationSettings = typeof question.responsevalidation === 'string'
          ? JSON.parse(question.responsevalidation || '{}')
          : (question.responsevalidation || {});

        if (question.validationType) {
          validationSettings.validationType = question.validationType;
        }

        if (question.validation_rules) {
          validationSettings.validation_rules = question.validation_rules;
        }

        responsevalidation = JSON.stringify(validationSettings);
      }

      const dbQuestion = {
        chatbot_id: id,
        text: question.text,
        type: question.type,
        order: question.order,
        options: question.options || null,
        shortname: question.shortname || null,
        errormessage: question.errormessage || null,
        responsevalidation: responsevalidation || null,
        validationType: question.validationType || 'none',
        enable_ai_response: question.enable_ai_response || false,
        ai_response_prompt: question.ai_response_prompt || null
      };

      const { data, error } = await supabase
        .from("questions")
        .insert([dbQuestion])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Immediately update the cache with the new question
      queryClient.setQueryData(["questions", id], (oldData: any) => {
        if (!oldData) return [data];
        return [...oldData, data];
      });
      
      // Then invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["questions", id] });
      toast({
        title: "Question added",
        description: "New question has been added successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const updateQuestion = useMutation({
    mutationFn: async ({ questionId, updates }: { questionId: string; updates: Partial<Question> }) => {
      if (!id) throw new Error("No chatbot ID provided");

      // Log the update operation to help with debugging
      console.log("Updating question:", questionId, "with updates:", updates);
      console.log("ValidationType being saved:", updates.validationType);

      const dbUpdates: any = {};

      if (updates.text !== undefined) dbUpdates.text = updates.text;
      if (updates.type !== undefined) dbUpdates.type = updates.type;
      if (updates.order !== undefined) dbUpdates.order = updates.order;
      if (updates.options !== undefined) dbUpdates.options = updates.options;
      if (updates.shortname !== undefined) dbUpdates.shortname = updates.shortname;
      if (updates.errormessage !== undefined) dbUpdates.errormessage = updates.errormessage;
      if (updates.rich_content !== undefined) dbUpdates.rich_content = updates.rich_content;
      if (updates.enable_ai_response !== undefined) dbUpdates.enable_ai_response = updates.enable_ai_response;
      if (updates.ai_response_prompt !== undefined) dbUpdates.ai_response_prompt = updates.ai_response_prompt;

      // Store validation settings in both the responsevalidation field and the dedicated validationType column
      if (updates.validationType !== undefined || updates.validation_rules !== undefined) {
        // Get the current validation settings or create a new object
        const validationSettings = typeof updates.responsevalidation === 'string'
          ? JSON.parse(updates.responsevalidation || '{}')
          : (updates.responsevalidation || {});

        // Update the validation settings
        if (updates.validationType !== undefined) {
          validationSettings.validationType = updates.validationType;
          // Also update the dedicated validationType column
          dbUpdates.validationType = updates.validationType;
        }

        if (updates.validation_rules !== undefined) {
          validationSettings.validation_rules = updates.validation_rules;
        }

        // Store the updated validation settings
        dbUpdates.responsevalidation = JSON.stringify(validationSettings);
      } else if (updates.responsevalidation !== undefined) {
        dbUpdates.responsevalidation = updates.responsevalidation;
      }

      console.log("Database updates being sent:", dbUpdates);

      const { error, data } = await supabase
        .from("questions")
        .update(dbUpdates)
        .eq("id", questionId)
        .eq("chatbot_id", id)
        .select();

      if (error) throw error;

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["questions", id] });
      console.log("Question updated successfully:", data);
      toast({
        title: "Question updated",
        description: "Question has been updated successfully.",
      });
    },
    onError: (error: any) => {
      console.error("Error updating question:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const updateQuestionsOrder = useMutation({
    mutationFn: async (questions: Question[]) => {
      if (!id) throw new Error("No chatbot ID provided");
      if (!questions || !Array.isArray(questions) || questions.length === 0) {
        throw new Error("No questions provided for order update");
      }

      // Log the update operation with detailed information
      console.log("Updating order for multiple questions:",
        questions.map(q => ({ id: q.id, order: q.order, text: q.text?.substring(0, 20) })));

      try {
        // Use a more robust approach - first get all questions to ensure we're working with current data
        const { data: existingQuestions, error: fetchError } = await supabase
          .from("questions")
          .select("id, order")
          .eq("chatbot_id", id);

        if (fetchError) throw fetchError;
        console.log("Existing questions before update:", existingQuestions);

        // Create array of update operations with explicit order values
        const updatePromises = questions.map((question, index) => {
          const explicitOrder = index; // Ensure order is exactly the index in the array
          console.log(`Setting question ${question.id} to order ${explicitOrder}`);

          return supabase
            .from("questions")
            .update({ order: explicitOrder })
            .eq("id", question.id)
            .eq("chatbot_id", id);
        });

        // Execute all updates in parallel
        const results = await Promise.all(updatePromises);

        // Check for errors
        const errors = results.filter(result => result.error);
        if (errors.length > 0) {
          throw new Error(`Failed to update questions: ${errors.map(e => e.error?.message).join(', ')}`);
        }

        // Verify the updates were successful
        const { data: updatedQuestions, error: verifyError } = await supabase
          .from("questions")
          .select("id, order")
          .eq("chatbot_id", id)
          .order("order", { ascending: true });

        if (verifyError) throw verifyError;
        console.log("Questions after update:", updatedQuestions);

        return results;
      } catch (error) {
        console.error("Error in updateQuestionsOrder:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questions", id] });
      console.log("Questions order updated successfully");
    },
    onError: (error: any) => {
      console.error("Error updating questions order:", error);
      toast({
        variant: "destructive",
        title: "Error updating question order",
        description: error.message || "Failed to save the new question order",
      });
    },
  });

  const deleteQuestion = useMutation({
    mutationFn: async (questionId: string) => {
      if (!id) throw new Error("No chatbot ID provided");
      const { error } = await supabase
        .from("questions")
        .delete()
        .eq("id", questionId)
        .eq("chatbot_id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questions", id] });
      toast({
        title: "Question deleted",
        description: "Question has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  return {
    addQuestion,
    updateQuestion,
    deleteQuestion,
    updateQuestionsOrder
  };
};
